/**
 * useTutorInvoicesState — les gestes de « Mes factures », et la génération d'un PDF.
 *
 * Port de `TutoringInvoicesScreen.js` : « Facture » ouvre le PDF (`attachment`) ; sans PDF, il le fait
 * générer (`generateFullPdf`, mesuré `invoice.generate`), la ligne dit « Génération... » et ne répond
 * plus à un second toucher, puis le PDF s'ouvre (la réponse remplace aussi la facture dans le
 * réducteur) ; un refus se dit dans le `<Snackbar>` de la page au lieu de la boîte de `useApiStatus`.
 * « Récapitulatif » ouvre `payment_recap`. La croix : la pile, sinon la coquille (`menu`).
 */
import { useState } from "react"

import { tutorsService } from "../../../../../../data/services/tutorsService"
import { type TutorInvoiceLinks } from "../../../../adapters/tutor/Account/tutorInvoiceAdapter"
import { type UITutorInvoice } from "../../../types/tutorCompte"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"

const INVOICE_GENERATION_FAILED = "La facture n'a pas pu être générée. Réessaie dans un instant."

export function useTutorInvoicesState(links: Record<number, TutorInvoiceLinks>) {
    const onClose = useBackOr("menu")
    const [generatingId, setGeneratingId] = useState<number | null>(null)
    const [notice, setNotice] = useState<string | null>(null)

    const { start } = useApiMutation({
        service: tutorsService.generateFullPdf,
        scopeParam: "id",
        gesture: "invoice.generate",
        surface: "tutor_invoices",
        fallback: INVOICE_GENERATION_FAILED,
        skipWhilePending: true,
        onSuccess: (data) => {
            setGeneratingId(null)
            openExternalUrl((data as { attachment?: string | null } | null)?.attachment)
        },
        onError: (message) => {
            setGeneratingId(null)
            setNotice(message)
        },
    })

    return {
        onClose,
        onOpenInvoice: (invoice: UITutorInvoice) => {
            const attachment = links[invoice.id]?.attachment
            if (attachment) {
                openExternalUrl(attachment)
                return
            }
            if (generatingId != null) return
            setNotice(null)
            if (start({ id: invoice.id })) setGeneratingId(invoice.id)
        },
        onOpenPaymentRecap: (invoice: UITutorInvoice) => openExternalUrl(links[invoice.id]?.paymentRecap),
        generatingInvoiceId: generatingId,
        notice,
        onDismissNotice: () => setNotice(null),
    }
}
